import React from "react";
import styles from "./Information.module.scss";

// How the panel sits in the copy: floated magazine panels the text wraps
// under (left/right), a full-width banner (top), or a free-standing panel
// that fills whatever box it is dropped into (fill), or a full-width closing
// panel rendered BELOW the copy (bottom).
type MediaPosition = "left" | "right" | "top" | "fill" | "bottom";

// Optional accent composed onto a floated panel: the wide scale step. Shapes
// that crop the source (square) are banned — art ships at its native ratio.
type MediaShape = "wide";

// Card-level architecture. Every section on a page wears a different one, so
// no two cards read as the same layout flipped:
//
//   float   — magazine panel sitting in the copy, text wrapping under it.
//             Steered by imagePosition / imageShape.
//   plate   — the card splits into a full-height art WALL down its left edge
//             and a recessed copy console beside it. SHORT sections only: the
//             wall covers to the console's height, so long copy would grind
//             the art down to a slot.
//   banner  — the section header lifts off the page and sits ON a full-bleed
//             art strip across the card's top, scrimmed into the copy below.
//   poster  — the art IS the card: a 4:3 backdrop the ring paints over, copy
//             overlaid in the art's quiet zone, the closing CTA pinned to the
//             card's bottom. SHORT sections only.
//
// Everything except "float" owns its own panel geometry, so imagePosition and
// imageShape are ignored there. All of them are gated on `image`: a card with
// no art is plain full-width prose whatever the variant says.
type CardVariant = "float" | "plate" | "banner" | "poster";

const positionClass: Record<MediaPosition, string> = {
  left: "media-left",
  right: "media-right",
  top: "media-top",
  fill: "media-fill",
  bottom: "media-bottom",
};

const shapeClass: Record<MediaShape, string> = {
  wide: "media-wide",
};

const variantClass: Record<CardVariant, string> = {
  float: "",
  plate: "content-plate",
  banner: "content-banner",
  poster: "content-poster",
};

// Where each architecture insists its panel goes. "float" is the only variant
// that hands the choice back to the caller.
const variantPosition: Record<CardVariant, MediaPosition | undefined> = {
  float: undefined,
  plate: "left",
  banner: "top",
  poster: "fill",
};

interface InformationMediaProps {
  image: string;
  alt?: string;
  position?: MediaPosition;
  shape?: MediaShape;
  // Breaks the panel THROUGH the card's chamfered ring: negative margins carry
  // the art past the card padding until it is flush with the card's outer
  // edge, so the ring stops where the art starts and the card's own chamfer
  // clips the corner. Which edges bleed follows from `position`; the panel
  // trades its own frame for a single gold cut-line down the edge that stays
  // inside the card.
  bleed?: boolean;
  // Chassis label bolted under the panel: display-face readout, tick cluster
  // and a hairline running out to the panel's edge.
  caption?: string;
  // object-position for the layouts that ask a 4:3 source to hold a strip or
  // a portrait — the region of THIS image that must survive the crop. Belongs
  // with the art, not with the layout: the same variant framing a different
  // picture wants a different window.
  focus?: string;
}

// Holo-framed image panel. Rendered by InformationComponent's image prop,
// or standalone anywhere inside a section's content flow (e.g. between two
// paragraphs). The <figure> is the layout shell — floats, widths, bleed — and
// the panel inside it carries the frame, so a caption bar can attach OUTSIDE
// the panel's clip-path instead of being clipped away by it.
export const InformationMedia: React.FC<InformationMediaProps> = ({
  image,
  alt,
  position = "fill",
  shape,
  bleed = false,
  caption,
  focus,
}: InformationMediaProps) => {
  const classes = [
    styles["frame"],
    styles[positionClass[position]],
    shape ? styles[shapeClass[shape]] : "",
    bleed ? styles["media-bleed"] : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <figure
      className={classes}
      style={
        focus ? ({ "--media-focus": focus } as React.CSSProperties) : undefined
      }>
      <div className={styles["media"]}>
        <img src={image} alt={alt ?? ""} loading="lazy" />
        <span className={styles["holo"]} aria-hidden="true" />
      </div>
      {caption && (
        <figcaption className={styles["caption"]}>{caption}</figcaption>
      )}
    </figure>
  );
};

interface InformationProps {
  icon: string | undefined;
  sizeX: number;
  sizeY: number;
  kicker?: string;
  title: string;
  image?: string;
  imageAlt?: string;
  imagePosition?: MediaPosition;
  imageShape?: MediaShape;
  imageCaption?: string;
  imageFocus?: string;
  variant?: CardVariant;
  // Opening statement pulled out of `children` so a layout can place it.
  // Rendered as the card's leading blockquote, identical to writing one
  // inline.
  lead?: React.ReactNode;
  // Float layouts only: render the lead full-width ABOVE the panel instead
  // of beside it. For cards whose lead would otherwise be squeezed into the
  // narrow column next to a wide float — a few words per line under a
  // too-long gold rule.
  leadAbove?: boolean;
  children: React.ReactNode;
}

export const InformationComponent: React.FC<InformationProps> = ({
  icon,
  sizeX,
  sizeY,
  kicker,
  title,
  image,
  imageAlt,
  imagePosition = "left",
  imageShape,
  imageCaption,
  imageFocus,
  variant = "float",
  lead,
  leadAbove = false,
  children,
}: InformationProps) => {
  // Every layout switch is gated on the art actually existing: a card without
  // it is plain full-width prose whatever the variant says.
  const layout: CardVariant = image === undefined ? "float" : variant;
  const position = variantPosition[layout] ?? imagePosition;
  // Only total interruptions may touch the card ring: the banner masthead and
  // the plate's art wall span their whole flank, so the ring closes cleanly
  // against them. Partial cuts read as broken lines, so floated and bottom
  // panels always keep their own chamfered frame.
  const bleed =
    layout === "banner" || layout === "plate" || layout === "poster";
  const mediaBlock = image ? (
    <InformationMedia
      image={image}
      alt={imageAlt}
      position={position}
      shape={layout === "float" ? imageShape : undefined}
      bleed={bleed}
      caption={imageCaption}
      focus={imageFocus}
    />
  ) : null;

  const kickerBlock = kicker ? (
    <span className={styles["kicker"]}>{kicker}</span>
  ) : null;

  const headerBlock = (
    <div className={styles["column"]}>
      <div className={styles["header"]}>
        <div className={styles["icon-wrapper"]}>
          <svg className={styles["icon"]} viewBox={`0 0 ${sizeX} ${sizeY}`}>
            <path
              fill="#ffffffbb"
              strokeWidth="10"
              stroke="#ffffff00"
              d={icon}
            />
          </svg>
        </div>
        <h2>{title}</h2>
      </div>
    </div>
  );

  const leadBlock = lead ? (
    <blockquote className={styles["lead"]}>{lead}</blockquote>
  ) : null;

  // A lead lifted above the panel spans the card full-width and the float
  // begins below it; everything else keeps the lead as the body's first
  // block.
  const liftLead = leadAbove && layout === "float";
  const body = (
    <>
      {!liftLead && leadBlock}
      {children}
    </>
  );

  const contentClass = `${styles["content"]} ${
    variantClass[layout] ? styles[variantClass[layout]] : ""
  }`.trim();

  return (
    <div className={styles["information"]}>
      {/* The banner takes the header off the page and onto the art, so the
          title plate above the card is rendered there instead of here. */}
      {layout !== "banner" && kickerBlock}
      {layout !== "banner" && headerBlock}
      <div className={contentClass}>
        {/* A lifted lead opens the card full-width; the float begins below
            it, so the quote is never squeezed into the column beside a wide
            panel. */}
        {liftLead && leadBlock}
        {position !== "bottom" && mediaBlock}
        {layout === "banner" && (
          <div className={styles["masthead"]}>
            {kickerBlock}
            {headerBlock}
          </div>
        )}
        {/* The split plate needs its copy in one box: the console is a grid
            column, and a loose run of paragraphs would each become one. */}
        {layout === "plate" ? (
          <div className={styles["console"]}>{body}</div>
        ) : (
          body
        )}
        {/* A closing panel: full-width art under the copy (and any CTA),
            still inside the card ring. */}
        {position === "bottom" && mediaBlock}
      </div>
    </div>
  );
};
